import { Chip } from '@mui/material';
import classes from './AnalyticsDashboard.module.css';
import { ArrowUpward, Article, Person, Group, CurrencyRupee, Badge } from '@mui/icons-material';
import Chart from './Chart';
import PieChartCustom from './PieChartCustom';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const AnalyticsDashboard = () => {
    const [dashboardData, setDashboardData] = useState({});
    const navigate = useNavigate();

    // Helper: financial year order (April = 1 through March = 12)
    const getMonthOrder = (monthName) => {
        const monthMap = {
            'April': 1,
            'May': 2,
            'June': 3,
            'July': 4,
            'August': 5,
            'September': 6,
            'October': 7,
            'November': 8,
            'December': 9,
            'January': 10,
            'February': 11,
            'March': 12
        };
        return monthMap[monthName] || 0;
    };

    // Get current financial year
    const getCurrentFinancialYear = () => {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth(); // 0-11, where 0 is January
        
        // Financial year starts from April (month 3)
        if (currentMonth >= 3) {
            // April to December - current year to next year
            return `${currentYear}-${currentYear + 1}`;
        } else {
            // January to March - previous year to current year
            return `${currentYear - 1}-${currentYear}`;
        }
    };

    // Sort grossPayEachMonth April → March (financial year) and add year to month names
    const sortedGrossPayData = useMemo(() => {
        if (!dashboardData.grossPayEachMonth || !Array.isArray(dashboardData.grossPayEachMonth)) {
            return [];
        }
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth();
        
        // Determine the financial year start year
        let fyStartYear = currentYear;
        if (currentMonth < 3) {
            fyStartYear = currentYear - 1;
        }
        
        // Month abbreviations
        const monthAbbr = {
            'January': 'Jan', 'February': 'Feb', 'March': 'Mar',
            'April': 'Apr', 'May': 'May', 'June': 'Jun',
            'July': 'Jul', 'August': 'Aug', 'September': 'Sep',
            'October': 'Oct', 'November': 'Nov', 'December': 'Dec'
        };
        
        return [...dashboardData.grossPayEachMonth].sort((a, b) => {
            return getMonthOrder(a.name) - getMonthOrder(b.name);
        }).map((item) => {
            const monthOrder = getMonthOrder(item.name);
            // Months April (1) to December (9) use fyStartYear
            // Months January (10) to March (12) use fyStartYear + 1
            const year = monthOrder <= 9 ? fyStartYear : fyStartYear + 1;
            const monthAbbreviation = monthAbbr[item.name] || item.name.substring(0, 3);
            return {
                ...item,
                name: `${monthAbbreviation} ${year}`
            };
        });
    }, [dashboardData.grossPayEachMonth]);

    // Get financial year string for display
    const financialYear = useMemo(() => {
        return getCurrentFinancialYear();
    }, []);

    // Calculate rounded up average age
    const roundedAvgAge = useMemo(() => {
        if (!dashboardData.avgEmployeeAge) return 0;
        return Math.ceil(Number(dashboardData.avgEmployeeAge));
    }, [dashboardData.avgEmployeeAge]);

    // Calculate total salary paid as sum of gross salaries from grossPayEachMonth
    const totalSalaryPaid = useMemo(() => {
        if (!dashboardData.grossPayEachMonth || !Array.isArray(dashboardData.grossPayEachMonth)) {
            return 0;
        }
        return dashboardData.grossPayEachMonth.reduce((sum, item) => {
            // The dataKey is "Gross Salary" based on Chart component
            const grossAmount = item["Gross Salary"] || item.grossSalary || item.value || 0;
            return sum + (Number(grossAmount) || 0);
        }, 0);
    }, [dashboardData.grossPayEachMonth]);

    const activeCounts = useMemo(() => {
        const v = dashboardData?.totalActiveEmployees;
        // Backward compatible: older API used a number
        if (typeof v === 'number') {
            return { employees: v, intern: 0, apprentice: 0 };
        }
        return {
            employees: Number(v?.employees) || 0,
            intern: Number(v?.intern) || 0,
            apprentice: Number(v?.apprentice) || 0
        };
    }, [dashboardData?.totalActiveEmployees]);

    useEffect(() => {
        (async () => {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company-master/is-loggedin`, {
            credentials: 'include',
            });
            if(response.status === 200) {
              const responseData = await response.json();
              setDashboardData(responseData);
            } else if(response.status === 401) {
              navigate('/');
            }
          })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/dashboard/get-dashboard-analytics`, {
                    credentials: 'include'
                });
                const responseData = await response.json();
                if(response.status === 200) {
                    setDashboardData(responseData);
                }
            } catch (error) {
            }
        })();
    }, []);

    return (
        <>
            <div className={[classes['dashboard-container']]}>
                <div className={[classes['dashboard-wrapper']]}>
                    <div className={classes["top"]}>
                        <div className={classes["card-section"]}>
                            <div className={classes["card"]}>
                                <div className={classes.cardSlider} aria-label="Active headcount slider">
                                    <div className={classes.cardSliderTrack}>
                                        <div className={classes.cardSlide}>
                                            <div className={classes["card-header"]}>
                                                <div className={classes["card-left"]}>
                                                    <p className={classes["card-title"]}>Active Employees</p>
                                                    <p className={classes["card-number"]}>{activeCounts.employees}</p>
                                                </div>
                                                <div className={classes["card-right"]} style={{backgroundColor: '#ff579a'}}>
                                                    <Group style={{fontSize: '30px'}}/>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={classes.cardSlide}>
                                            <div className={classes["card-header"]}>
                                                <div className={classes["card-left"]}>
                                                    <p className={classes["card-title"]}>Active Intern</p>
                                                    <p className={classes["card-number"]}>{activeCounts.intern}</p>
                                                </div>
                                                <div className={classes["card-right"]} style={{backgroundColor: '#0b418e'}}>
                                                    <Person style={{fontSize: '30px'}}/>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={classes.cardSlide}>
                                            <div className={classes["card-header"]}>
                                                <div className={classes["card-left"]}>
                                                    <p className={classes["card-title"]}>Active Apprentice</p>
                                                    <p className={classes["card-number"]}>{activeCounts.apprentice}</p>
                                                </div>
                                                <div className={classes["card-right"]} style={{backgroundColor: '#607d8b'}}>
                                                    <Badge style={{fontSize: '30px'}}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={classes["card-footer"]}>
                                    <div className={classes["chip"]}>
                                        <Chip icon={<ArrowUpward style={{color: '#00cea1'}} /> } label="30%" style={{backgroundColor: '#ccf5e7', color: '#00cea1'}} />
                                    </div>
                                    <span className={classes["card-description"]}>
                                       Till Today
                                    </span>
                                </div>
                            </div>
                            <div className={classes["card"]}>
                                <div className={classes["card-header"]}>
                                    <div className={classes["card-left"]}>
                                        <p className={classes["card-title"]}>Average Employee Age</p>
                                        <p className={classes["card-number"]}>{roundedAvgAge}</p>
                                    </div>
                                    <div className={classes["card-right"]} style={{backgroundColor: '#0b418e'}} >
                                        <Person style={{fontSize: '30px'}}/>
                                    </div>
                                </div>
                                {/* <div className={classes["card-footer"]}>
                                    <div className={classes["chip"]}>
                                        <Chip icon={<ArrowUpward style={{color: '#00cea1'}} /> } label="30%" style={{backgroundColor: '#ccf5e7', color: '#00cea1'}} />
                                    </div>
                                    <span className={classes["card-description"]}>
                                        Since last month
                                    </span>
                                </div> */}
                            </div>
                            <div className={classes["card"]}>
                                <div className={classes["card-header"]}>
                                    <div className={classes["card-left"]}>
                                        <p className={classes["card-title"]}>Leave Days</p>
                                        <p className={classes["card-number"]}>10</p>
                                    </div>
                                    <div className={classes["card-right"]} style={{backgroundColor: '#0b418e'}}>
                                        <Article style={{fontSize: '30px'}}/>
                                    </div>
                                </div>
                                <div className={classes["card-footer"]}>
                                    <div className={classes["chip"]}>
                                        <Chip icon={<ArrowUpward style={{color: '#00cea1'}} /> } label="30%" style={{backgroundColor: '#ccf5e7', color: '#00cea1'}} />
                                    </div>
                                    <span className={classes["card-description"]}>
                                       Current month
                                    </span>
                                </div>
                            </div>
                            <div className={classes["card"]}>
                                <div className={classes["card-header"]}>
                                    <div className={classes["card-left"]}>
                                        <p className={classes["card-title"]}>Total Salary Paid</p>
                                        <p className={classes["card-number"]}>Rs. {totalSalaryPaid.toLocaleString('en-IN')}</p>
                                    </div>
                                    <div className={classes["card-right"]} style={{backgroundColor: '#FF8C00'}}>
                                        <CurrencyRupee style={{fontSize: '30px'}}/>
                                    </div>
                                </div>
                                <div className={classes["card-footer"]}>
                                    <div className={classes["chip"]}>
                                        <Chip icon={<ArrowUpward style={{color: '#00cea1'}} /> } label="30%" style={{backgroundColor: '#ccf5e7', color: '#00cea1'}} />
                                    </div>
                                    <span className={classes["card-description"]}>
                                        Current FY
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className={classes["chart-section"]}>
                            <PieChartCustom data={dashboardData.genderDistribution}/>
                        </div>
                    </div>
                    <div className={classes["bottom"]}>
                        <Chart data={sortedGrossPayData} title={`Gross Salary Chart (FY ${financialYear})`} grid dataKey="Gross Salary" />
                    </div>
                </div>
            </div>
        </>
    );
}

export default AnalyticsDashboard;
